import type { ReactNode } from 'react';
import { Button } from './Button';
import './EmptyState.css';

/**
 * Friendly empty state for list pages. The brief disallows leaving a
 * coach staring at "no data" with no path forward — every list-shaped
 * surface gets one of these when its query returns empty.
 *
 * Compose with a contextual eyebrow + one primary CTA (the obvious next
 * step). Optional secondary text gives a hint when the empty state has
 * a soft "you can also" affordance.
 */
interface EmptyStateProps {
  /** Big short headline — the one-line answer to "why is this empty?". */
  title: string;
  /** Body explanation, 1-2 sentences max. Voice-friendly. */
  body?: ReactNode;
  /** Primary CTA — the obvious next step. */
  cta?: { label: string; onClick: () => void };
  /** Optional secondary hint or link beneath the CTA. */
  hint?: ReactNode;
  /** Lightweight glyph or emoji rendered above the title. Keep simple — *
   *  decoration only, don't load icons. */
  glyph?: ReactNode;
}

export function EmptyState({ title, body, cta, hint, glyph }: EmptyStateProps) {
  return (
    <div className="empty-state" role="status">
      {glyph && <div className="empty-state__glyph" aria-hidden>{glyph}</div>}
      <h3 className="empty-state__title">{title}</h3>
      {body && <p className="empty-state__body">{body}</p>}
      {cta && (
        <Button variant="primary" size="touchline" onClick={cta.onClick}>
          {cta.label}
        </Button>
      )}
      {hint && <div className="empty-state__hint">{hint}</div>}
    </div>
  );
}
